/**
 * ReplenishmentDrawer (SR3.1) — the 補貨建議 drill-down.
 *
 * Read-only by contract: it renders the derived `ReorderSuggestion` (SR1.1) as a
 * formula WATERFALL with every input attributed to its provenance —
 *   可供量@syncedAt (Tier-1 read-through) · 在途量 (Tier-2 derived open-PO) ·
 *   待出量 (demand pool) · 再訂購點/目標水位 (Tier-2 config) · MOQ/倍數 clamps ·
 *   NetSuite 參考值 delta footnote — then deep-links out to the product detail,
 *   the OCC order lines (product-scoped, SR2.2), and the demand pool. Nothing is
 *   edited here (a suggestion is derived-never-stored; edits happen on the draft
 *   PO the planner creates from the queue row).
 *
 * Width is PINNED and every waterfall row fits ONE line at that width with
 * width-bounded (tabular) numerics — progressive disclosure (the MOQ/倍數/參考值
 * trace is a compact footnote block, not a wrapping stack) per ux-lens finding 4.
 */

import { Button, Drawer, Space, Typography, theme } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import SourceStampTag from '@/components/common/SourceStampTag';
import type { ReorderSuggestionRow } from '@/types/api/reorder';

const { Text, Title } = Typography;

export interface ReplenishmentDrawerProps {
  open: boolean;
  suggestion: ReorderSuggestionRow | null;
  onClose: () => void;
}

/** One waterfall line: label left, width-bounded numeric right — always one line. */
interface WaterfallLineProps {
  label: string;
  value: number;
  emphasis?: boolean;
  sign?: '+' | '−';
}

const ReplenishmentDrawer = ({ open, suggestion, onClose }: ReplenishmentDrawerProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['procurement', 'products', 'common']);
  const { token } = theme.useToken();

  // Keep the drawer mounted through the close animation even after the row clears.
  if (!suggestion) {
    return <Drawer open={false} onClose={onClose} width={520} />;
  }

  const availableQty = suggestion.inventoryPosition - suggestion.qtyOpenToReceive;
  const syncedStamp = dayjs(suggestion.syncedAt).format('MM-DD HH:mm');

  const sectionTitleStyle = {
    fontSize: token.fontSize,
    fontWeight: token.fontWeightStrong,
    marginBottom: token.marginSM,
  };

  const WaterfallLine = ({ label, value, emphasis, sign }: WaterfallLineProps) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: token.marginSM,
        padding: `${token.paddingXS}px 0`,
        borderBottom: `1px solid ${token.colorSplit}`,
      }}
    >
      <Text
        style={{
          color: emphasis ? token.colorText : token.colorTextSecondary,
          fontWeight: emphasis ? token.fontWeightStrong : undefined,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: emphasis ? token.fontWeightStrong : undefined,
          whiteSpace: 'nowrap',
        }}
      >
        {sign ? `${sign} ` : ''}
        {value}
      </Text>
    </div>
  );

  const links: Array<{ key: string; label: string; onClick: () => void }> = [
    {
      key: 'product',
      label: t('procurement:demandPool.reorder.drawer.links.product'),
      onClick: () => navigate(`/auth/products/${suggestion.productId}`),
    },
    {
      key: 'orderLines',
      label: t('procurement:demandPool.reorder.drawer.links.orderLines'),
      // OCC lenses scope on the canonical internalProductId (SR2.2).
      onClick: () => navigate(`/auth/occ/lines?productId=${suggestion.internalProductId}`),
    },
    {
      key: 'pool',
      label: t('procurement:demandPool.reorder.drawer.links.pool'),
      onClick: () => navigate('/auth/purchase-orders/demand-pool/shortage'),
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      destroyOnHidden
      title={
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span>{suggestion.sku}</span>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM, fontWeight: 'normal' }}>
            {suggestion.productName} · {suggestion.internalProductId}
          </Text>
        </div>
      }
    >
      {/* Provenance chip — freshness is owned here (the 可供量 read-through moment). */}
      <div className="tw-mb-6">
        <SourceStampTag
          sourceLabel={t('products:itemLocations.source')}
          syncedAt={suggestion.syncedAt}
        />
      </div>

      {/* Formula waterfall — every input attributed to its tier. */}
      <div style={{ marginBottom: token.marginLG }}>
        <Title level={5} style={sectionTitleStyle}>
          {t('procurement:demandPool.reorder.drawer.title')}
        </Title>
        <WaterfallLine
          label={t('procurement:demandPool.reorder.drawer.waterfall.availableQty', {
            syncedAt: syncedStamp,
          })}
          value={availableQty}
        />
        <WaterfallLine
          label={t('procurement:demandPool.reorder.drawer.waterfall.onOrderQty')}
          value={suggestion.qtyOpenToReceive}
          sign="+"
        />
        <WaterfallLine
          label={t('procurement:demandPool.reorder.drawer.waterfall.position')}
          value={suggestion.inventoryPosition}
          emphasis
        />
        <WaterfallLine
          label={t('procurement:demandPool.reorder.drawer.waterfall.pooledDemand')}
          value={suggestion.pooledDemandQty}
        />
        <WaterfallLine
          label={t('procurement:demandPool.reorder.drawer.waterfall.reorderPoint')}
          value={suggestion.rop}
        />
        <WaterfallLine
          label={t('procurement:demandPool.reorder.drawer.waterfall.targetLevel')}
          value={suggestion.psl}
        />
        {suggestion.moqClamped && suggestion.moq != null && (
          <WaterfallLine
            label={t('procurement:demandPool.reorder.drawer.waterfall.moqClamp')}
            value={suggestion.moq}
          />
        )}
        {suggestion.multipleRounded && (
          <WaterfallLine
            label={t('procurement:demandPool.reorder.drawer.waterfall.multipleRound')}
            value={suggestion.suggestedQty}
          />
        )}
        <WaterfallLine
          label={t('procurement:demandPool.reorder.drawer.waterfall.suggestedQty')}
          value={suggestion.suggestedQty}
          emphasis
        />
      </div>

      {/* Trace footnotes — MOQ/multiple provenance + NetSuite reconciliation delta. */}
      {(suggestion.moqClamped ||
        suggestion.multipleRounded ||
        suggestion.netsuiteDelta != null) && (
        <Space direction="vertical" size={4} style={{ marginBottom: token.marginLG }}>
          {(suggestion.moqClamped || suggestion.multipleRounded) && (
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('procurement:demandPool.reorder.suggestedQtyRoundedFrom', {
                raw: suggestion.rawQty,
              })}
            </Text>
          )}
          {suggestion.netsuiteDelta != null && (
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('procurement:demandPool.reorder.drawer.referenceFootnote', {
                qty: suggestion.netsuiteQtyOnOrder ?? 0,
                delta: suggestion.netsuiteDelta,
              })}
            </Text>
          )}
        </Space>
      )}

      {/* Deep links out — the referencing surface never edits the source. */}
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {links.map((link) => (
          <Button
            key={link.key}
            type="link"
            icon={<ExportOutlined />}
            style={{ padding: 0, height: 'auto', lineHeight: 1.4 }}
            onClick={() => {
              link.onClick();
              onClose();
            }}
          >
            {link.label}
          </Button>
        ))}
      </Space>

      {/* Advisory-only reminder — a suggestion never creates a PO on its own. */}
      <Text
        type="secondary"
        className="tw-block tw-mt-6"
        style={{ fontSize: token.fontSizeSM }}
      >
        {t('procurement:demandPool.reorder.subtitle')}
      </Text>
    </Drawer>
  );
};

export default ReplenishmentDrawer;
